import React, { useState } from 'react';

interface PDFPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  formData: any;
}

const PDFPreviewModal: React.FC<PDFPreviewModalProps> = ({ isOpen, onClose, formData }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isHtmlPreview, setIsHtmlPreview] = useState(false);

  const generatePreview = async () => {
    setLoading(true);
    setError(null);
    setPdfUrl(null);
    setIsHtmlPreview(false);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/forms/preview-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const contentType = response.headers.get('content-type');
        console.log('Response content type:', contentType);
        
        if (contentType && contentType.includes('text/html')) {
          // Handle HTML response (when Puppeteer is not available)
          const htmlText = await response.text();
          const htmlBlob = new Blob([htmlText], { type: 'text/html' });
          const url = URL.createObjectURL(htmlBlob);
          setPdfUrl(url);
          setIsHtmlPreview(true);
        } else {
          // Handle PDF response
          const pdfBlob = await response.blob();
          const url = URL.createObjectURL(pdfBlob);
          setPdfUrl(url);
          setIsHtmlPreview(false);
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to generate PDF preview');
      }
    } catch (error) {
      console.error('Error generating PDF preview:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setError(`Feil ved generering av PDF forhåndsvisning: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  // Generate preview when modal opens
  React.useEffect(() => {
    if (isOpen && !pdfUrl) {
      generatePreview();
    }
  }, [isOpen]);

  // Cleanup object URL when modal closes
  React.useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const handleClose = () => {
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
    setError(null);
    setIsHtmlPreview(false);
    onClose();
  };

  const downloadPDF = () => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = 'salgsskjema-preview.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="pdf-preview-overlay" onClick={handleClose}>
      <div className="pdf-preview-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pdf-preview-header">
          <h2>{isHtmlPreview ? 'HTML Forhåndsvisning' : 'PDF Forhåndsvisning'}</h2>
          {isHtmlPreview && (
            <div className="preview-notice">
              <span>⚠️ Viser HTML-versjon (PDF-generering er ikke tilgjengelig)</span>
            </div>
          )}
          <div className="pdf-preview-actions">
            {pdfUrl && (
              <button 
                className="btn btn-secondary"
                onClick={downloadPDF}
                title="Last ned PDF"
              >
                📥 Last ned
              </button>
            )}
            <button 
              className="btn btn-secondary"
              onClick={generatePreview}
              disabled={loading}
              title="Oppdater forhåndsvisning"
            >
              🔄 {loading ? 'Laster...' : 'Oppdater'}
            </button>
            <button 
              className="close-button"
              onClick={handleClose}
            >
              ×
            </button>
          </div>
        </div>
        
        <div className="pdf-preview-content">
          {loading && (
            <div className="pdf-loading">
              <div className="spinner"></div>
              <p>Genererer PDF forhåndsvisning...</p>
              <p className="loading-note">Dette kan ta noen sekunder første gang.</p>
            </div>
          )}
          
          {error && (
            <div className="pdf-error">
              <div className="error-icon">⚠️</div>
              <h3>Feil ved PDF-generering</h3>
              <p>{error}</p>
              <button 
                className="btn btn-primary"
                onClick={generatePreview}
              >
                Prøv igjen
              </button>
            </div>
          )}
          
          {pdfUrl && !loading && (
            <div className="pdf-viewer">
              <iframe
                src={pdfUrl}
                width="100%"
                height="100%"
                style={{ border: 'none' }}
                title="PDF Preview"
              />
            </div>
          )}
        </div>
        
        <div className="pdf-preview-footer">
          <p className="pdf-info">
            {isHtmlPreview 
              ? 'Denne HTML-forhåndsvisningen viser innholdet som vil bli konvertert til PDF når systemet er fullstendig konfigurert.'
              : 'Denne forhåndsvisningen viser hvordan PDF-en vil se ut når skjemaet sendes.'
            }
          </p>
          <div className="pdf-footer-actions">
            <button 
              className="btn btn-secondary"
              onClick={handleClose}
            >
              Lukk
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PDFPreviewModal;